import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, queryOptions, type QueryFunction } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useAuthedMutation } from "@/lib/authed-mutation";
import { globalQueryFn } from "@/lib/query-registry";
import { updateReportSettings, type ReportSettingsDto } from "@/lib/report-settings.functions";

const reportSettingsQuery = () =>
  queryOptions({
    queryKey: ["admin.report-settings.get"] as const,
    queryFn: globalQueryFn as QueryFunction<{ settings: ReportSettingsDto }>,
    staleTime: 0,
  });

export const Route = createFileRoute("/admin/settings/report")({
  head: () => ({
    meta: [
      { title: "Report Settings · Admin · Shugert" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ReportSettingsPage,
});

function ReportSettingsPage() {
  const q = useQuery(reportSettingsQuery());
  const settings = q.data?.settings;

  const [form, setForm] = useState<ReportSettingsDto | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  const m = useAuthedMutation(updateReportSettings, {
    invalidate: [["admin.report-settings.get"]],
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
  });

  const update = <K extends keyof ReportSettingsDto>(k: K, v: ReportSettingsDto[K]) => {
    setForm((prev) => (prev ? { ...prev, [k]: v } : prev));
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    const { updated_at: _u, ...payload } = form;
    void _u;
    m.mutate(payload);
  };

  return (
    <div className="space-y-8">
      <header>
        <Link to="/admin/settings" className="text-xs text-muted-foreground hover:text-foreground">
          ← Settings
        </Link>
        <h1 className="mt-2 font-display text-2xl font-semibold text-foreground">
          Report Delivery &amp; Branding
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Controls the signature, header, footer, contact info and confidentiality notice rendered
          in the Pro report delivery email. Also sets how long signed download links stay valid.
        </p>
      </header>

      {q.isPending && <p className="text-sm text-muted-foreground">Loading…</p>}
      {q.isError && (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {q.error?.message}
        </div>
      )}

      {form && (
        <form
          onSubmit={onSubmit}
          className="space-y-6 rounded-2xl border border-border bg-card/30 p-6"
        >
          <Section title="Signature">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Name"
                value={form.signature_name}
                onChange={(v) => update("signature_name", v)}
              />
              <Field
                label="Title"
                value={form.signature_title}
                onChange={(v) => update("signature_title", v)}
              />
            </div>
            <TextArea
              label="Custom HTML signature (optional)"
              help="If set, this replaces the name/title block. Supports tokens: {client}, {domain}, {score}, {plan}."
              value={form.signature_block_html}
              onChange={(v) => update("signature_block_html", v)}
              rows={4}
            />
          </Section>

          <Section title="Contact info">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Contact email"
                value={form.contact_email}
                onChange={(v) => update("contact_email", v)}
              />
              <Field
                label="Contact phone"
                value={form.contact_phone}
                onChange={(v) => update("contact_phone", v)}
              />
            </div>
            <Field
              label="Calendly URL"
              value={form.calendly_url}
              onChange={(v) => update("calendly_url", v)}
            />
          </Section>

          <Section title="Email header &amp; footer (HTML)">
            <TextArea
              label="Header HTML (optional override)"
              help="Leave blank to keep the default gold-eyebrow header. Supports {client}, {domain}, {score}, {plan}."
              value={form.email_header_html}
              onChange={(v) => update("email_header_html", v)}
              rows={5}
            />
            <TextArea
              label="Footer HTML (optional override)"
              help="Leave blank to keep the default Shugert footer."
              value={form.email_footer_html}
              onChange={(v) => update("email_footer_html", v)}
              rows={4}
            />
          </Section>

          <Section title="Confidentiality &amp; access">
            <TextArea
              label="Confidentiality notice"
              help="Rendered as a styled banner inside every delivery email. Use {client} to insert the recipient name."
              value={form.confidentiality_notice}
              onChange={(v) => update("confidentiality_notice", v)}
              rows={3}
            />
            <div className="grid gap-1">
              <label className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Signed link lifetime (hours)
              </label>
              <input
                type="number"
                min={1}
                max={168}
                value={form.report_link_ttl_hours}
                onChange={(e) => update("report_link_ttl_hours", parseInt(e.target.value, 10) || 1)}
                className="mt-2 w-40 rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:border-foreground focus:outline-none"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                How long the download link in the delivery email stays valid. Default 24h. Max 168h
                (7 days).
              </p>
            </div>
          </Section>

          {m.isError && (
            <p className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {m.error.message}
            </p>
          )}
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={m.isPending}
              className="rounded-full bg-foreground px-5 py-2 text-sm font-medium text-background hover:opacity-90 disabled:opacity-50"
            >
              {m.isPending ? "Saving…" : "Save"}
            </button>
            {saved && <span className="text-xs text-muted-foreground">Saved ✓</span>}
          </div>
        </form>
      )}

      <details className="rounded-2xl border border-border bg-card/20 p-5 text-sm">
        <summary className="cursor-pointer font-medium text-foreground">How this works</summary>
        <div className="mt-4 space-y-3 text-muted-foreground">
          <p>
            When you click <strong>Deliver report</strong> on an order, the app:
          </p>
          <ol className="ml-5 list-decimal space-y-1.5">
            <li>Generates a signed download URL valid for the configured lifetime.</li>
            <li>
              Wraps your .docx download link, score card, signature, header and footer (all from
              this page) into a confidential delivery email.
            </li>
            <li>Sends it via Resend.</li>
            <li>
              Records the signed-URL issuance in <code>report_access_log</code>.
            </li>
            <li>
              Flips order status to <code>complete</code>.
            </li>
          </ol>
          <p>
            The same access log records every portal download. View it on the order detail page
            under "Access log".
          </p>
        </div>
      </details>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-4 border-b border-border pb-6 last:border-0 last:pb-0">
      <h2
        className="font-display text-base font-semibold text-foreground"
        dangerouslySetInnerHTML={{ __html: title }}
      />
      {children}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-foreground focus:border-foreground focus:outline-none"
      />
    </div>
  );
}

function TextArea({
  label,
  help,
  value,
  onChange,
  rows = 4,
}: {
  label: string;
  help?: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <div>
      <label className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
        {label}
      </label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="mt-2 w-full rounded-xl border border-border bg-background px-3 py-2.5 font-mono text-xs text-foreground focus:border-foreground focus:outline-none"
      />
      {help && <p className="mt-2 text-xs text-muted-foreground">{help}</p>}
    </div>
  );
}
